const THEME_STYLESHEET_SELECTOR = 'link[rel~="stylesheet"][href*="/themes/fbs-theme/assets/"]';
const POLL_INTERVAL = 750;

function withCacheBuster(url) {
  const nextUrl = new URL(url, window.location.href);
  nextUrl.searchParams.set('fbs-theme-live', Date.now().toString());
  return nextUrl.toString();
}

function getStylesheetBaseUrl(link) {
  return link?.href ? link.href.split('?')[0] : '';
}

/**
 * Reload every compiled theme stylesheet while `npm run dev` is active.
 *
 * The theme builds global CSS and every ACF block as separate Parcel entries.
 * The old reloader only watched assets/index.css, so edits in individual
 * block style.scss files rebuilt correctly but the browser kept displaying
 * the already-loaded block stylesheet until a full clean/restart.
 */
export function enableLiveStyleReload() {
  if (!window.wp_vars?.theme_dev_mode) {
    return;
  }

  const watchedStylesheets = new Map();

  const discoverStylesheets = () => {
    document.querySelectorAll(THEME_STYLESHEET_SELECTOR).forEach((link) => {
      const baseUrl = getStylesheetBaseUrl(link);

      if (!baseUrl || watchedStylesheets.has(baseUrl)) {
        return;
      }

      watchedStylesheets.set(baseUrl, {
        currentLink: link,
        previousCss: null,
        checking: false,
      });
    });
  };

  const checkStylesheet = async (baseUrl, state) => {
    if (state.checking) {
      return;
    }

    state.checking = true;

    try {
      const response = await fetch(withCacheBuster(baseUrl), { cache: 'no-store' });

      if (!response.ok) {
        return;
      }

      const css = await response.text();

      if (state.previousCss === null) {
        state.previousCss = css;
        return;
      }

      if (css === state.previousCss) {
        return;
      }

      state.previousCss = css;

      const oldStylesheet = state.currentLink;
      const replacement = oldStylesheet.cloneNode();
      replacement.removeAttribute('id');
      replacement.disabled = false;
      replacement.href = withCacheBuster(baseUrl);

      replacement.addEventListener('load', () => {
        state.currentLink = replacement;
        oldStylesheet.remove();
        document.dispatchEvent(new CustomEvent('fbs-theme:styles-reloaded', {
          detail: { href: baseUrl },
        }));
      }, { once: true });

      oldStylesheet.after(replacement);
    } catch (error) {
      // The next polling cycle retries after a partial build or server restart.
    } finally {
      state.checking = false;
    }
  };

  const checkForChanges = () => {
    discoverStylesheets();

    watchedStylesheets.forEach((state, baseUrl) => {
      checkStylesheet(baseUrl, state);
    });
  };

  document.documentElement.dataset.fbsThemeLiveStyles = 'enabled';

  discoverStylesheets();
  checkForChanges();
  window.setInterval(checkForChanges, POLL_INTERVAL);
}
